/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import styled from 'styled-components';
import PostureModalLayout from 'components/Modal/PostureModal/PostureModalLayout';
import ModalPortal from 'components/Modal/ModalPortal';
import ChartLabel from 'components/Label/ChartLabel';
import useHorizontalBarData from 'hooks/useHorizontalBarData';
import { chartColor, color } from 'asserts/styles/colors';
import { commonTooltipOptions } from 'consts/chartOptions';
import { postureLabels } from 'consts/constants';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { CSSTransition } from 'react-transition-group';

import { ReactComponent as IconPostureLeg } from 'asserts/icons/Icon_Posture_Leg_2.svg';
import { ReactComponent as IconArrowDown } from 'asserts/icons/ic_arrow_rdown.svg';
import { ReactComponent as IconArrowUp } from 'asserts/icons/ic_arrow_up.svg';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface PostureStatisticsChartAProps {
  chartData: PostureData;
}

const PostureStatisticsReverseBarChart = ({
  chartData
}: PostureStatisticsChartAProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const [isOPenModal, setIsOpenModal] = useState<boolean>(false);
  const [titleText, setTitleText] = useState<string>('');
  const [detailText, setDetailText] = useState<string>('');
  const [directions, setDirections] = useState<string[]>([]);

  const [selectedChartType, setSelectedChartType] = useState<string | null>(
    null
  );

  const footGaugeLabels = ['회내', '정상', '회외'];
  const chartType = ['leftfoot', 'rightfoot'];

  const { graphOrange, graphYellow, graphGreen, graphRed } = chartColor;

  const footColors = [
    graphRed,
    graphOrange,
    graphYellow,
    graphGreen,
    graphYellow,
    graphOrange,
    graphRed
  ];

  const leftfoot = chartData?.leftfoot || [];
  const rightfoot = chartData?.rightfoot || [];

  const leftFootData = [
    leftfoot.pserious,
    leftfoot.pseverity,
    leftfoot.plight,
    leftfoot.normal,
    leftfoot.slight,
    leftfoot.sseverity,
    leftfoot.sserious
  ];

  const rightFootData = [
    rightfoot.pserious,
    rightfoot.pseverity,
    rightfoot.plight,
    rightfoot.normal,
    rightfoot.slight,
    rightfoot.sseverity,
    rightfoot.sserious
  ];

  const postureTypes = [
    {
      title: '발과회내외',
      detail: '좌',
      data: leftFootData,
      icon: <IconPostureLeg />,
      labels: postureLabels,
      colors: footColors,
      directions: ['회내', '회외'],
      gaugeLabels: footGaugeLabels
    },
    {
      title: '발과회내외',
      detail: '우',
      data: rightFootData,
      icon: <IconPostureLeg />,
      labels: postureLabels,
      colors: footColors,
      directions: ['회내', '회외'],
      gaugeLabels: footGaugeLabels
    }
  ];

  const options = {
    layout: {
      padding: {
        right: 90
      }
    },
    indexAxis: 'y' as const,
    plugins: { legend: { display: false }, tooltip: commonTooltipOptions },
    scales: {
      x: {
        display: false
      },
      y: {
        grid: {
          display: false
        },

        ticks: {
          padding: 5,
          color: `${color.white}`,
          font: {
            size: 13,
            family: 'Noto Sans KR'
          }
        }
      }
    }
  };

  const handleClickOpenModal = (index: number) => {
    setIsOpenModal(true);
    const selectedChartType = chartType[index];

    setSelectedChartType(selectedChartType);
  };

  const handleClickCloseModal = () => {
    setIsOpenModal(false);
  };

  return (
    <>
      {postureTypes.map((posture, index) => (
        <PostureChart
          data={posture.data}
          key={index}
          icon={posture.icon}
          title={posture.title}
          detail={posture.detail}
          colors={posture.colors}
          labels={posture.labels}
          gaugeLabels={posture.gaugeLabels}
          directions={posture.directions}
          options={options}
          setDirections={setDirections}
          setTitleText={setTitleText}
          setDetailText={setDetailText}
          onClickModal={() => handleClickOpenModal(index)}
        />
      ))}
      <CSSTransition
        in={isOPenModal}
        classNames="modal"
        nodeRef={nodeRef}
        timeout={300}
        unmountOnExit
      >
        <ModalPortal>
          <PostureModalLayout
            colors={footColors}
            labels={postureLabels}
            titleText={titleText}
            detailText={detailText}
            directions={directions}
            selectedChartType={selectedChartType}
            onClickModal={handleClickCloseModal}
          />
        </ModalPortal>
      </CSSTransition>
    </>
  );
};

const PostureChart = ({
  icon,
  title,
  detail,
  colors,
  options,
  labels,
  data: chartData,
  onClickModal,
  setTitleText,
  setDetailText,
  setDirections,
  directions
}: any) => {
  const data = {
    labels,
    datasets: [
      {
        label: `${title}Data`,
        data: chartData,
        backgroundColor: colors,
        borderColor: colors,
        barThickness: 6,
        borderRadius: 3
      }
    ]
  };

  const addDataPlugins = {
    id: 'addDataPlugins',
    afterDatasetsDraw: useHorizontalBarData('posture')
  };

  const handleClickChart = (e: React.MouseEvent) => {
    setTitleText(title);
    setDetailText(detail);
    setDirections(directions);

    onClickModal();
    e.stopPropagation();
  };

  const uniqueLabels: string[] = Array.from(new Set(labels));
  const uniqueColos: string[] = Array.from(new Set(colors));

  const reverseUniqueColos = uniqueColos.reverse();

  return (
    <Wrapper onClick={handleClickChart}>
      <TitleWrapper>
        {icon}
        <TextBox>
          <PositionTitle>{title}</PositionTitle>
          <PositionDetail>{detail}</PositionDetail>
        </TextBox>
      </TitleWrapper>
      <ChartWrapper>
        <Bar data={data} options={options} plugins={[addDataPlugins]} />
        {directions && (
          <SubDirection>
            <div>
              <IconArrowUp />
              <p>{directions[0]}</p>
            </div>

            <div>
              <p>{directions[1]}</p>
              <IconArrowDown />
            </div>
          </SubDirection>
        )}
      </ChartWrapper>
      <ChartLabelWrapper>
        {uniqueLabels
          ?.reverse()
          .map((label: string, index: number) => (
            <ChartLabel
              key={`${title}${label}`}
              pointColor={reverseUniqueColos[index]}
              text={label}
            />
          ))}
      </ChartLabelWrapper>
    </Wrapper>
  );
};

export default PostureStatisticsReverseBarChart;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  width: 600px;
  height: 340px;
  margin-bottom: 16px;
  background: ${color.white005};
  border-radius: 16px;
  cursor: pointer;
`;

const TitleWrapper = styled.div`
  display: flex;
  padding: 12px 12px 0;
`;

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 12px;
  text-align: left;
`;

const PositionTitle = styled.p``;

const PositionDetail = styled.p`
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.3px;
  color: ${chartColor.graphMobile};
`;

const ChartWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 80px;
  height: 200px;
`;

const ChartLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  justify-content: center;
  padding-bottom: 24px;
`;

const SubDirection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 80px;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.3px;

  & p {
    padding: 4px 0 0 0;
  }

  & div:last-of-type p {
    padding: 0 0 4px 0;
  }
`;
